import React, { Component } from 'react';
import { CountriesData } from 'app-types';
import { find, unionBy, upperFirst, isEmpty } from 'lodash-es';
import * as utils from 'utils/utils';
import { SelectValue } from 'antd/lib/select';
import { Alert, Autocomplete, Input } from 'components/antd';
import { ListAlert } from 'components';
import classNames from 'classnames';
import styles from './PhoneInput.module.scss';
import { ThemeContext } from 'app-themes';
import { WrappedFormUtils } from 'antd/lib/form/Form';

const FE_VALIDATION_ERROR = 'phone code not found, select from the suggested list to continue';

export interface PhoneInputData {
	phoneNumber: string;
	phoneCode?: string;
	phoneCodeCountryId: number;
}

interface Props {
	countries: CountriesData[];
	initialValue: PhoneInputData;
	form: WrappedFormUtils;
	keyName?: string;
	structureName?: string;
	prefix?: string;
	required: boolean;
	onChange?: () => void;
}

interface State {
	countriesDictionary: CountriesData[];
	isFEValidationError?: boolean;
}

export class PhoneInput extends Component<Props, State> {
	isChanged?: boolean;
	static contextType = ThemeContext;
	static defaultProps = {
		required: true,
		initialValue: {
			phoneNumber: undefined,
			phoneCode: undefined,
			phoneCodeCountryId: undefined,
		},
	};

	constructor(props: Props) {
		super(props);
		this.state = {
			countriesDictionary: unionBy<CountriesData>(props.countries, 'countryPhoneCode'),
		};
	}

	componentDidUpdate(_prevProps: Props, prevState: State) {
		const { isFEValidationError } = this.state;
		if (isFEValidationError !== prevState.isFEValidationError) {
			this.props.form.validateFields({ force: true });
		}
	}

	getName = (ownName: string) => {
		const { keyName, structureName, prefix } = this.props;
		if (keyName) {
			return `${keyName}[${ownName}]`;
		}
		if (structureName) {
			return `${structureName}.${ownName}`;
		}
		if (prefix) {
			return `${prefix}${upperFirst(ownName)}`;
		}
		return ownName;
	};

	getInitialPhoneCode = () => {
		const {
			countries,
			initialValue: { phoneCode, phoneCodeCountryId },
		} = this.props;
		if (!phoneCode) {
			return utils.getPhoneCodeByCountryId(countries, phoneCodeCountryId);
		}
		return phoneCode;
	};

	onChange = () => {
		const { onChange } = this.props;
		if (onChange) {
			onChange();
		}
		this.isChanged = false;
		this.setState({
			isFEValidationError: false,
		});
	};

	onPhoneSearch = (value: SelectValue) => {
		this.isChanged = true;

		if (!value) {
			this.setState({
				isFEValidationError: false,
			});
		}
	};

	onBlur = (value: SelectValue) => {
		if (value && this.isChanged) {
			this.setState({
				isFEValidationError: true,
			});
		}
	};

	getAlertsInfo = () => {
		const alerts = [];
		if (this.state.isFEValidationError) {
			alerts.push({
				messages: [FE_VALIDATION_ERROR],
				listHeader: 'Phone number:',
			});
		}

		return alerts;
	};

	render() {
		const { initialValue, form, required } = this.props;
		const { countriesDictionary, isFEValidationError } = this.state;
		const { getFieldDecorator, setFieldsValue } = form;
		const theme = this.context;
		const alerts = this.getAlertsInfo();
		return (
			<div className={styles.phoneInputWrapper}>
				{getFieldDecorator(this.getName('phoneCode'), {
					initialValue: this.getInitialPhoneCode(),
					rules: [
						{
							validator: (_rule: unknown, value: string, callback) => {
								const checkEmptyValue = required ? value : true;

								callback(
									!isFEValidationError && checkEmptyValue
										? undefined
										: 'Incorrect phone code'
								);
							},
						},
					],
				})(
					<Autocomplete
						dataSource={countriesDictionary.map(country => ({
							value: country.countryPhoneCode,
							text: country.countryPhoneCode,
						}))}
						allowClear
						className={classNames(styles.phoneInputCode, {
							[styles[`phoneInputCode-${theme}`]]: theme !== '',
						})}
						onSelect={countryPhoneCode => {
							let countryId;
							if (typeof countryPhoneCode === 'string') {
								const country = find(countriesDictionary, { countryPhoneCode });
								if (country) {
									countryId = country.countryId;
								}
							}
							setFieldsValue({
								[this.getName('phoneCodeCountryId')]: countryId,
							});
							this.onChange();
						}}
						onChange={countryPhoneCode => {
							if (isEmpty(countryPhoneCode)) {
								setFieldsValue({
									[this.getName('phoneCodeCountryId')]: null,
								});
								this.onChange();
							}
						}}
						onSearch={this.onPhoneSearch}
						onBlur={this.onBlur}
						getPopupContainer={el => (el && el.parentElement) || document.body}
						filterOption={(input: any, option: any) =>
							option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
					/>
				)}
				{getFieldDecorator(this.getName('phoneNumber'), {
					initialValue: initialValue.phoneNumber,
					rules: [
						{
							required,
							whitespace: true,
						},
					],
				})(<Input onChange={this.onChange} className={styles.phoneInputNumber} />)}

				{getFieldDecorator(this.getName('phoneCodeCountryId'), {
					initialValue: initialValue.phoneCodeCountryId,
					rules: [
						{
							required,
							whitespace: true,
							type: 'number',
						},
					],
				})(<Input type={'hidden'} />)}
				{alerts.length > 0 &&
					alerts.map((item, index) => {
						const message = (
							<ListAlert errors={item.messages || []} listHeader={item.listHeader} />
						);
						return (
							<Alert
								type="error"
								message={message}
								showIcon
								key={`${item.listHeader}-${index}`}
							/>
						);
					})}
			</div>
		);
	}
}
